"use client";

import React, { useState, useEffect } from 'react';
import { X, Send, Sparkles, RefreshCw } from 'lucide-react';

interface RebuildCustomPromptProps {
  onSubmit: (prompt: string) => void;
  onClose?: () => void;
  isRebuilding?: boolean;
}

export const RebuildCustomPrompt = ({ onSubmit, onClose, isRebuilding = false }: RebuildCustomPromptProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [isVisuallyLoading, setIsVisuallyLoading] = useState(false);

  // 視覚的フィードバックの遅延制御
  useEffect(() => {
    if (isRebuilding) {
      setIsVisuallyLoading(true);
    } else if (isVisuallyLoading) {
      // 4秒遅延してから視覚的フィードバックを終了
      const timer = setTimeout(() => {
        setIsVisuallyLoading(false);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [isRebuilding, isVisuallyLoading]);

  const handleSubmit = () => {
    if (customPrompt.trim()) {
      onSubmit(customPrompt);
      setCustomPrompt("");
      setIsOpen(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setCustomPrompt("");
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey && customPrompt.trim()) {
      handleSubmit();
    }
  };

  return (
    <div className="relative">
        <span className="relative inline-flex">
            {isOpen ? (
            <div 
            className="absolute right-0 mb-4 p-6 rounded-2xl shadow-lg bg-white dark:bg-black border border-white/20 dark:border-gray-700/30 dark:border dark:border-solid dark:border-white dark:border-1"
            style={{
                width: '320px',
                top: 'calc(100% + 0.5rem)',
                right: '0',
                animation: 'slideUp 0.3s ease-out forwards',
                zIndex: 50
            }}
            >
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                レポート再生成
                </h3>
                <button
                onClick={handleClose}
                className="p-1 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 rounded-full transition-colors"
                aria-label="閉じる"
                >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
            </div>
            
            <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="AIに指示するプロンプトを入力してください...（Ctrl/Command + Enterで送信）"
                className="w-full p-3 mb-4 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black-500/50 min-h-[120px] resize-none"
            />
            
            <div className="flex justify-end">
                <button
                onClick={handleSubmit}
                disabled={isVisuallyLoading || !customPrompt.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black/90 hover:bg-black/95 disabled:bg-black/40 disabled:cursor-not-allowed text-white font-medium transition-colors backdrop-blur-sm cursor-pointer dark:border dark:border-solid dark:border-white"
                aria-label="送信"
                >
                {isVisuallyLoading ? (
                    <>
                    <Sparkles className="h-4 w-4 animate-pulse" />
                    <span>処理中...</span>
                    </>
                ) : (
                    <>
                    <Send className="h-4 w-4" />
                    <span>送信</span>
                    </>
                )}
                </button>
            </div>
            </div>
            ) : (
                <button
                type="button"
                onClick={() => setIsOpen(true)}
                disabled={isVisuallyLoading}
                className={`glass-prompt-button h-[40px] w-[40px] flex items-center justify-center rounded-full shadow-lg hover:shadow-xl transition-all duration-500 backdrop-blur-md ${isVisuallyLoading 
                  ? 'bg-gray-600/90 text-white border border-gray-400/60 cursor-not-allowed shadow-gray-400/25' 
                  : 'bg-black/80 hover:bg-black/90 text-white border border-black/30 dark:border-white/30 dark:hover:bg-white/10 cursor-pointer'
                }`}
                aria-label={isVisuallyLoading ? "AI処理中..." : "再生成"}
                title={isVisuallyLoading ? "AIが処理中です" : "AIでレポートを再生成"}
                >
                {isVisuallyLoading ? (
                  <RefreshCw className="h-5 w-5 animate-spin text-white" />
                ) : (
                  <Sparkles className="h-5 w-5" />
                )}
                </button>
            )}
        </span>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.1);
          }
          80% {
            opacity: 1;
            transform: scale(0.89);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .glass-prompt-form {
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
        }

        .glass-prompt-button {
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.2);
        }

        .glass-prompt-button:hover {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
}; 